import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Building2, Users, FileText, CreditCard, TrendingUp, MessageSquare, Clock, IndianRupee } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, subtitle, color = 'indigo' }) => (
  <Card className="bg-slate-900 border-slate-800" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`h-12 w-12 rounded-xl bg-${color}-600/20 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PlatformDashboard = () => {
  const { apiFetch } = usePlatformAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await apiFetch('/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch platform stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  const formatINR = (num) => {
    if (!num) return '₹0';
    return '₹' + num.toLocaleString('en-IN');
  };

  return (
    <div data-testid="platform-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Platform Dashboard</h1>
        <p className="text-slate-400">Overview of all schools and platform metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Schools" value={stats?.total_tenants || 0} icon={Building2} subtitle={`${stats?.active_tenants || 0} active`} />
        <StatCard title="Total Users" value={stats?.total_users || 0} icon={Users} subtitle="Across all schools" color="emerald" />
        <StatCard title="Monthly Revenue" value={formatINR(stats?.mrr)} icon={IndianRupee} subtitle={`ARR: ${formatINR(stats?.arr)}`} color="amber" />
        <StatCard title="Leave Requests" value={stats?.total_leave_requests || 0} icon={FileText} subtitle={`${stats?.pending_requests || 0} pending`} color="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Plan Distribution */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.plan_distribution && Object.keys(stats.plan_distribution).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(stats.plan_distribution).map(([plan, count]) => {
                  const total = stats.total_tenants || 1;
                  const percent = Math.round((count / total) * 100);
                  const colors = { trial: 'bg-slate-500', starter: 'bg-blue-500', professional: 'bg-indigo-500', enterprise: 'bg-amber-500' };
                  return (
                    <div key={plan}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-300 capitalize">{plan}</span>
                        <span className="text-slate-400">{count} ({percent}%)</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${colors[plan] || 'bg-indigo-500'}`} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No data yet</p>
            )}
          </CardContent>
        </Card>

        {/* State Distribution */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Schools by State</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.state_distribution && Object.keys(stats.state_distribution).length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {Object.entries(stats.state_distribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([state, count]) => (
                    <div key={state} className="flex justify-between items-center">
                      <span className="text-slate-300 text-sm">{state}</span>
                      <span className="bg-slate-800 text-slate-300 text-xs font-bold px-2.5 py-1 rounded-full">{count}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">New Signups (30d)</p>
              <p className="text-2xl font-bold text-white">{stats?.recent_signups_30d || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Unread Messages</p>
              <p className="text-2xl font-bold text-white">{stats?.unread_messages || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-red-600/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Inactive Schools</p>
              <p className="text-2xl font-bold text-white">{stats?.inactive_tenants || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlatformDashboard;
